import { HttpEvent, HttpEventType, HttpHeaders, HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';
import { BucketCompartmentEnum } from '../../models/enums/bucket-compartment.enum';
import { FileTypeEnum } from '../../models/enums/mime-type.enum';
import { MediaProxy } from '../../models/interfaces/media.interface';
import { AWSSignedProxy } from '../../models/interfaces/aws-signed.interface';
import { Api } from "../api/api";
import { environment } from '../../../../environments/environment';
import { isFakeBackendEnabled } from '../../utils/fake-backend.util';

export interface UploadOptions {
  compartment?: BucketCompartmentEnum;
  isPrivate?: boolean;
  folder?: string;
  prefix?: string;
  contentType?: string;
  maxFileSize?: number;
  allowedTypes?: string[];
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

@Injectable({
  providedIn: 'root',
})
export class UploadService {

  private readonly api: Api = inject(Api);

  private readonly httpClient: HttpClient = inject(HttpClient);

  private readonly progressSubject: Subject<UploadProgress> = new Subject<UploadProgress>();


  public get progress$(): Observable<UploadProgress> {
    return this.progressSubject.asObservable();
  }

  public async uploadPublicImage(file: File): Promise<string> {
    return this.uploadPublicFile(file, 'images');
  }

  public async uploadPublicFile(file: File, folder: 'images' | 'documents' | 'videos'): Promise<string> {
    const body = new FormData();
    body.append('file', file);

    const uploaded = await this.api.postAsync<{ publicUrl?: string }>(
      `${environment.routes.medias[folder]}?isPrivate=false`,
      body,
    );

    if (!uploaded?.publicUrl) {
      throw new Error('Não foi possível enviar o arquivo.');
    }

    return uploaded.publicUrl;
  }

  public async uploadFile(file: File, options: UploadOptions = {}): Promise<MediaProxy> {
    try {
      this.validateFile(file, options);

      if (isFakeBackendEnabled()) {
        return this.uploadFileMocked(file, options);
      }

      const compartment = options.compartment || BucketCompartmentEnum.PUBLIC;
      const isPrivate = options.isPrivate || false;

      const signedUrl = await this.getSignedUrl(file.name, compartment, isPrivate, options);

      await this.uploadToStorage(signedUrl.url, file, options.contentType);

      const readUrl = await this.getReadUrl(file.name, compartment, isPrivate);

      return {
        url: readUrl,
        fileName: file.name,
        fileType: this.getFileType(file),
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(`Erro no upload: ${message}`);
    }
  }

  public async uploadFileMocked(file: File, options: UploadOptions = {}): Promise<MediaProxy> {
    this.validateFile(file, options);

    const compartment = options.compartment || BucketCompartmentEnum.PUBLIC;
    this.progressSubject.next({ loaded: file.size, total: file.size, percentage: 100 });

    return {
      url: URL.createObjectURL(file) || `https://fake-cdn.local/${compartment}/${encodeURIComponent(file.name)}`,
      fileName: file.name,
      fileType: this.getFileType(file),
    };
  }

  public async getReadUrl(filename: string, compartment: BucketCompartmentEnum, isPrivate: boolean): Promise<string> {
    if (isFakeBackendEnabled()) {
      return this.getReadUrlMocked(filename, compartment, isPrivate);
    }

    const url = environment.routes.medias.presignedByFile
      .replace('{asset}', compartment)
      .replace('{file}', filename)
      .concat(`?isPrivate=${isPrivate}`);

    const response = await this.api.getAsync<{ url: string }>(url);

    if (!response) {
      throw new Error('Não foi possível obter a URL de leitura');
    }

    return response.url;
  }

  public async getReadUrlMocked(
    filename: string,
    compartment: BucketCompartmentEnum,
    isPrivate: boolean,
  ): Promise<string> {
    return `https://fake-cdn.local/${compartment}/${encodeURIComponent(filename)}?isPrivate=${isPrivate}`;
  }

  private validateFile(file: File, options: UploadOptions): void {
    if (!file || file.size === 0) {
      throw new Error('Arquivo inválido ou vazio');
    }

    if (options.maxFileSize && file.size > options.maxFileSize) {
      const maxSizeMB = options.maxFileSize / (1024 * 1024);
      throw new Error(`Arquivo muito grande. Tamanho máximo: ${maxSizeMB}MB`);
    }

    if (options.allowedTypes && !options.allowedTypes.includes(file.type)) {
      throw new Error(`Tipo de arquivo não permitido. Tipos aceitos: ${options.allowedTypes.join(', ')}`);
    }
  }

  private async getSignedUrl(filename: string, compartment: BucketCompartmentEnum, isPrivate: boolean, options: UploadOptions): Promise<AWSSignedProxy> {
    if (!filename.trim()) {
      throw new Error('Nome do arquivo é obrigatório');
    }

    const url = `${environment.routes.medias.presigned}${compartment}?isPrivate=${isPrivate}`;
    
    const payload: any = { filename };
    
    if (options.folder) {
      payload.folder = options.folder;
    }
    
    if (options.prefix) {
      payload.prefix = options.prefix;
    }

    const response = await this.api.postAsync<AWSSignedProxy>(url, payload);

    if (!response) {
      throw new Error('Não foi possível gerar a URL assinada');
    }

    return response;
  }

  private async uploadToStorage(url: string, file: File, contentType?: string): Promise<void> {
    const headers = new HttpHeaders({
      'Content-Type': contentType || this.getContentType(file),
    });

    let uploadError: Error | null = null;

    const uploadObservable = this.httpClient.put<void>(url, file, {
      headers,
      reportProgress: true,
      observe: 'events',
    }).pipe(
      tap((event: HttpEvent<void>) => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          this.progressSubject.next({
            loaded: event.loaded,
            total: event.total,
            percentage: Math.round((event.loaded / event.total) * 100)
          });
        }
      }),
      filter((event: HttpEvent<void>) => event.type === HttpEventType.Response),
      map(() => void 0),
      catchError((error: unknown) => {
        const message = error instanceof Error ? error.message : 'Erro desconhecido';
        uploadError = new Error(`Erro no upload: ${message}`);

        return of(void 0);
      })
    );

    await firstValueFrom(uploadObservable);

    if (uploadError) {
      throw uploadError;
    }
  }

  private getFileType(file: File): string {
    const extension = file.name.split('.').pop()?.toLowerCase();
    
    if (!extension) return FileTypeEnum.OTHER;

    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
    const documentExtensions = ['pdf', 'doc', 'docx', 'txt', 'rtf', 'odt'];
    const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'];
    const audioExtensions = ['mp3', 'wav', 'ogg', 'aac', 'flac'];

    if (imageExtensions.includes(extension)) return FileTypeEnum.IMAGE;
    if (documentExtensions.includes(extension)) return FileTypeEnum.DOCUMENT;
    if (videoExtensions.includes(extension)) return FileTypeEnum.VIDEO;
    if (audioExtensions.includes(extension)) return FileTypeEnum.AUDIO;

    return FileTypeEnum.OTHER;
  }

  private getContentType(file: File): string {
    const extension = file.name.split('.').pop()?.toLowerCase();
    
    const mimeTypes: Record<string, string> = {
      'md': 'text/markdown',
      'ttf': 'font/ttf',
      'woff': 'font/woff',
      'woff2': 'font/woff2',
      'otf': 'font/otf',
      'eot': 'application/vnd.ms-fontobject',
      'cur': 'image/x-win-bitmap',
      'bcmap': 'application/octet-stream',
      'js': 'application/javascript',
      'xml': 'application/xml',
    };

    if (extension && mimeTypes[extension]) {
      return mimeTypes[extension];
    }

    if (file.name.endsWith('LICENSE') || file.name.endsWith('TODO') || file.name.endsWith('.properties')) {
      return 'text/plain';
    }

    return file.type || 'application/octet-stream';
  }

}
